'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import UILayer from 'UIComponents/layer/UILayer';
import UIAbstractProgress from 'UIComponents/progress/UIAbstractProgress';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import UIIFrame from 'ui-addon-iframeable/host/UIIFrame';
import { URANUS_LAYER } from 'HubStyleTokens/sizes';
import { MSG_TYPE_MODAL_DIALOG_CLOSE } from 'ui-addon-iframeable/messaging/IFrameMessageTypes';
const fadeInAnimation = keyframes(["from{opacity:0}to{opacity:1}"]); // Transparent "sheet of glass" overlay that covers the entire window
// Rendered at a sill
// See https://git.hubteam.com/HubSpot/hub-details-ui/blob/master/static/js/components/DetailsView/FullScreenExpandable.js

export const FullScreenOverlay = styled.div.withConfig({
  displayName: "ModalIFrameLayer__FullScreenOverlay",
  componentId: "a32vfa-0"
})(["position:fixed;top:0;left:0;bottom:0;right:0;z-index:", ";opacity:0;animation-name:", ";animation-duration:100ms;animation-fill-mode:forwards;", ";", ";& > iframe{width:100%;height:100%;}"], URANUS_LAYER, fadeInAnimation, ({
  $show
}) => !$show && 'display: none', ({
  $loading
}) => $loading && `background-color: rgba(45,62,80,0.79)`);
/**
 * `ModalIFrameLayer` simulates a `UIModalIFrame` in a fullscreen transparent
 * iframe covering the window.
 *
 * To support components like `UIPanel` that can render inside the embedded
 * app, but require access to the full window aread, show a fullscreen
 * transparent `UIIFrame`, and have the embedded app render the `UIModal`.
 *
 * This allows components rendered inside the iframe to "break out" of
 * the modal's box, which they wouldn't be able to with `UIModalIFrame`.
 *
 * UIComponents has no officially supported way of rendering a fullscreen
 * transparent layer. For instance, `<UIModal use="fullscreen">` shows an
 * opaque white sheet with a shadow underneath it. Instead we use a custom
 * "sheet of glass" layer (a transparent div in a portal, at a high z-index,
 * covering the entire window) and render a 100% `UIIFrame` inside that.
 *
 * Coordination with the embedded app is required to rebuild the
 * convenience features of `UIModalIFrame`:
 *
 * 1. show a progress bar while the iframe loads
 * 2. show a translucent gray background while the iframe loads (to
 *    give the user immediate feedback, and to approximate the background that
 *    will be shown behind the modal)
 * 3. interpret the special `MSG_TYPE_MODAL_DIALOG_CLOSE` message and
 *    convert it to an `onClose` call
 */

const ModalIFrameLayer = ({
  actionButtons,
  embeddedPassthruProps,
  height,
  iframePassthruProps,
  onClose,
  onInitError,
  onMessage,
  onReady,
  onReadyError,
  show,
  showProgressIndicator,
  src,
  title,
  width
}) => {
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleMessage = useCallback(msg => {
    const {
      payload: {
        type
      }
    } = msg,
          restPayload = _objectWithoutPropertiesLoose(msg.payload, ["type"]);

    switch (type) {
      case MSG_TYPE_MODAL_DIALOG_CLOSE:
        onClose(Object.assign({}, restPayload));
        break;

      case 'ON_OPEN_START':
        setIsModalOpen(true);
        break;

      case 'ON_CLOSE_COMPLETE':
        setIsModalOpen(false);
        break;

      default:
        if (onMessage) {
          onMessage(msg);
        }

        break;
    }
  }, [onClose, onMessage]); // communication established with embedded app; stop showing gray background
  // (the embedded app's /layer route is responsible for rendering its own)

  const handleReady = useCallback(() => {
    setLoading(false);

    if (onReady) {
      onReady();
    }
  }, [onReady]);
  return /*#__PURE__*/_jsx(UILayer, {
    children: /*#__PURE__*/_jsxs(FullScreenOverlay, {
      $loading: loading,
      $show: show || isModalOpen,
      children: [showProgressIndicator && loading && /*#__PURE__*/_jsx(UIAbstractProgress, {
        render: ({
          value,
          done
        }) => /*#__PURE__*/_jsx(UINanoProgress, {
          value: value,
          animateOnComplete: done
        })
      }), /*#__PURE__*/_jsx(UIIFrame, {
        width: "100%",
        height: "100%",
        iframePassthruProps: iframePassthruProps,
        embeddedPassthruProps: Object.assign({}, embeddedPassthruProps, {
          title,
          actionButtons,
          height,
          width,
          show
        }),
        onInitError: onInitError,
        onMessage: handleMessage,
        onReady: handleReady,
        onReadyError: onReadyError,
        src: src
      })]
    })
  });
};

ModalIFrameLayer.propTypes = {
  actionButtons: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    props: PropTypes.object
  })).isRequired,
  embeddedPassthruProps: PropTypes.object,
  height: PropTypes.number,
  iframePassthruProps: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onInitError: PropTypes.func,
  onMessage: PropTypes.func,
  onReady: PropTypes.func,
  onReadyError: PropTypes.func,
  show: PropTypes.bool,
  showProgressIndicator: PropTypes.bool,
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
  width: PropTypes.number
};
export default ModalIFrameLayer;